import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";

// importing custom modules
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../components/Dialog";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { Button, LabelBox } from "../../components";
import { updateStatusSchema } from "../../yup";
import { Input } from "../../components/UserInput";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import Logs from "../../assets/images/logs.png";

// importing css and svg images
import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import style from "./surgeon.module.css";
import { colors } from "../../theme/colors";
import { DEFAULT_SUPER_ADMIN_ROLE_ID } from "../../utils/constants";

// importing services modules
import {
  useGetSurgeonsQuery,
  useRemoveDoctorFromHealthOrgMutation,
  useUpdateDoctorMutation,
} from "../../services/api";
import { RootState, useAppSelector } from "../../store";

type FormState = typeof updateStatusSchema.__outputType;
type SurgeonWithSpecialityName = Surgeon & { speciality_name: string };
type SurgeonAction = "view" | "logs" | "deassociate" | "deactivate" | "activate";

/**
 * Lists surgeons with search, activation/deactivation, and detail navigation controls.
 *
 * Returns:
 *   JSX.Element: Surgeon table with actions.
 *
 * Notes:
 *   - Uses debounced search and RTK Query for data retrieval.
 *   - Activation/deactivation uses confirmation and optional reason capture.
 */
const Surgeons = () => {
  const [removeDoctorFromHealthOrg] = useRemoveDoctorFromHealthOrgMutation();
  const [updateDoctor] = useUpdateDoctorMutation();
  const navigate = useNavigate();

  const { userDetails } = useAppSelector((state: RootState) => state.auth);
  const { specialities } = useAppSelector(
    (state: RootState) => state.referenceData
  );

  const specialityMap = useMemo(
    () => new Map(specialities.map((s) => [s.id, s.name])),
    [specialities]
  );

  // all query parameters and pagination params
  // Access control: action availability depends on role and health org scope.
  const isSuperAdmin =
    userDetails?.user_role_id === DEFAULT_SUPER_ADMIN_ROLE_ID;
  const healthOrgId = userDetails?.health_org_id;
  const canDeassociate = !isSuperAdmin || !!healthOrgId;
  const canDeactivate = isSuperAdmin && !healthOrgId;

  const [queryParam, setQueryParam] = useState<{
    search_key?: string;
    health_org_ids: number | undefined;
    is_include_all_doctor: boolean;
    is_detailed: boolean;
    order_by_columns: string;
    order: string;
    doctor_id: number | undefined;
    is_active?: boolean;
  }>({
    search_key: undefined,
    health_org_ids: isSuperAdmin ? undefined : healthOrgId,
    is_include_all_doctor: isSuperAdmin,
    is_detailed: true,
    order_by_columns: "surname",
    order: "Asc",
    doctor_id: undefined,
    is_active: isSuperAdmin ? undefined : true,
  });

  const [searchTerm, setSearchTerm] = useState(
    queryParam.search_key ?? ""
  );
  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSurgeon, setSelectedSurgeon] = useState<Surgeon | null>(null);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);

  const {
    data: surgeonData,
    refetch,
    isFetching,
  } = useGetSurgeonsQuery(queryParam, {
    // Super admins should be able to fetch across all health orgs.
    skip: !isSuperAdmin && !healthOrgId,
  });

  // Ensure we use the correct health org once user details load (e.g., after refresh).
  useEffect(() => {
    setQueryParam((prev) => ({
      ...prev,
      health_org_ids: isSuperAdmin ? undefined : healthOrgId,
      is_include_all_doctor: isSuperAdmin,
      is_active: isSuperAdmin ? undefined : true,
    }));
  }, [healthOrgId, isSuperAdmin]);

  const surgeonTableData = useMemo<SurgeonWithSpecialityName[]>(() => {
    const items = surgeonData?.items ?? [];
    return items.map((surgeon: Surgeon) => ({
      ...surgeon,
      speciality_name: surgeon.speciality_id
        ? specialityMap.get(surgeon.speciality_id) ?? ""
        : "",
    }));
  }, [specialityMap, surgeonData]);

  /**
   * Routes menu actions for a selected surgeon row.
   *
   * Args:
   *   value (SurgeonAction): Action selected from the row menu.
   *   surgeon (Surgeon): Surgeon row context for the action.
   *
   * Returns:
   *   void
   *
   * Notes:
   *   - Navigation and modal toggles are handled locally.
   */
  const handleMenu = (value: SurgeonAction, surgeon: Surgeon) => {
    setSelectedSurgeon(surgeon);
    if (value === "deassociate") {
      setModalVisible(true);
    } else if (value === "deactivate" || value === "activate") {
      setStatusModalVisible(true);
    } else if (value === "view") {
      navigate(`/surgeons/details/${surgeon?.id}`);
    } else if (value === "logs") {
      setLogModalVisible(true);
    }
  };

  const fields = [
    {
      id: "surname",
      label: "Name",
      flex: 2.5,
      sortable: true,
      type: "surname",
      render: (item: NewSurgeon) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item.surname?.toUpperCase() ?? ""}, ${item.given_name ?? ""} ${item.prefix ?? ""
            }`}
        </Typography>
      ),
    },
    {
      id: "speciality_name",
      label: "Speciality",
      flex: 2,
      sortable: true,
      render: (item: SurgeonWithSpecialityName) => {
        const specialityName =
          item.speciality_name ||
          specialityMap.get(item.speciality_id ?? 0) ||
          "";
        return (
          <Typography
            className={style.cell}
            sx={{
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={specialityName}
          >
            {specialityName}
          </Typography>
        );
      },
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 0.8,
      sortable: true,
      render: (item: SurgeonWithSpecialityName) => (
        <StatusBadge isActive={item.is_active ?? false} />
      ),
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      render: (item: Surgeon) => (
        <CustomActionMenu<Surgeon, SurgeonAction>
          item={item}
          isActive={item?.is_active ?? false}
          handleChange={handleMenu}
          actions={
            ([
              {
                label: "View",
                value: "view",
                icon: <EyeIcon height={24} width={24} />,
              },
              {
                label: "Logs",
                value: "logs",
                icon: <img src={Logs} alt="Logs" width={24} />,
              },
              ...(canDeassociate
                ? ([
                  {
                    label: "Deassociate",
                    value: "deassociate",
                    icon: <DeactivateIcon height={24} width={24} />,
                  },
                ] as ActionItem<SurgeonAction>[])
                : canDeactivate
                  ? ([
                    {
                      label: item.is_active ? "Deactivate" : "Activate",
                      value: item.is_active ? "deactivate" : "activate",
                      icon: item.is_active ? (
                        <DeactivateIcon height={24} width={24} />
                      ) : (
                        <PowerSettingsNewIcon height={24} width={24} />
                      ),
                    },
                  ] as ActionItem<SurgeonAction>[])
                  : []),
            ] as ActionItem<SurgeonAction>[])
          }
        />
      ),
    },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  // for searching in the query
  /**
   * Updates local search term used to debounce query params.
   *
   * Args:
   *   e (React.ChangeEvent<HTMLInputElement>): Input change event.
   *
   * Returns:
   *   void
   */
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value ?? "");
    },
    []
  );

  // Debounced search keeps API load manageable while user types.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: searchTerm === "" ? undefined : searchTerm,
      }));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  /**
   * Deassociates the selected surgeon from the current health org.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   *   - Requires a selected surgeon and health org context.
   *   - Updates UI message state based on mutation response.
   */
  // Guard against invalid payloads; backend expects valid ids for deassociation.
  const handleDeassociate = async () => {
    const doctorId = selectedSurgeon?.id;
    const healthOrgId = userDetails?.health_org_id;
    if (!doctorId || !healthOrgId) {
      setMessage({
        type: "error",
        text: "Something went wrong!",
      });
      return;
    }
    setIsLoading(true);
    try {
      await removeDoctorFromHealthOrg({
        doctor_id: doctorId,
        health_org_id: healthOrgId,
      }).unwrap();
      setMessage({
        type: "success",
        text: "Doctor deassociated successfully.",
      });
      refetch();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggles active status for the selected surgeon.
   *
   * Args:
   *   deactivateReason (string): Reason for deactivation; empty when activating.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   *   - Requires a selected surgeon.
   *   - Uses RTK Query mutation for status updates.
   */
  // Status toggle drives activation/deactivation and is gated by selection.
  const handleStatusUpdate = async (deactivateReason: string) => {
    if (!selectedSurgeon?.id) {
      setMessage({
        type: "error",
        text: "Something went wrong!",
      });
      return;
    }
    const latestSelection =
      surgeonTableData.find((surgeon) => surgeon.id === selectedSurgeon.id) ??
      selectedSurgeon;
    setIsLoading(true);
    try {
      await updateDoctor({
        id: latestSelection.id,
        is_active: !latestSelection.is_active,
        deactivate_reason: deactivateReason,
      }).unwrap();
      setMessage({
        type: "success",
        text: `Surgeon ${!latestSelection.is_active ? "activated" : "deactivated"
          } successfully.`,
      });
      setReasonModalVisible(false);
      setValue("deactivate_reason", "");
      refetch();
    } catch (error: any) {
      setMessage({
        type: "error",
        text:
          error?.message ||
          error?.data?.detail ||
          "Something went wrong!",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Submits the deactivate reason form.
   *
   * Args:
   *   values (FormState): Form values captured by react-hook-form.
   *
   * Returns:
   *   Promise<void>
   */
  const onDeactivateSubmit = async (values: FormState) => {
    await handleStatusUpdate(values.deactivate_reason ?? "");
  };

  // Render the Surgeon component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchTerm}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label={"Add New Surgeon"}
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<SurgeonWithSpecialityName>
          fields={fields}
          paginationRequired={true}
          data={surgeonTableData}
          loading={isFetching}
          onDoubleClick={(surgeon: Surgeon) => {
            navigate(`/surgeons/details/${surgeon?.id}`);
          }}
          defaultOrderBy={"surname"}
          searchTerm={searchTerm}
          onClearSearch={() => setSearchTerm("")}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={(() => {
            const surName = selectedSurgeon?.surname ?? "";
            const givenName = selectedSurgeon?.given_name ?? "";
            const prefix = selectedSurgeon?.prefix ?? "";
            const formatted = `${surName}, ${givenName} ${prefix}`.trim();
            return `Are you sure you want to deassociate ${formatted}?`;
          })()}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            handleDeassociate();
          }}
        />
      )}
      {statusModalVisible && selectedSurgeon && (
        <ConfirmationDialog
          visible={statusModalVisible}
          message={(() => {
            const surName = selectedSurgeon?.surname ?? "";
            const givenName = selectedSurgeon?.given_name ?? "";
            const prefix = selectedSurgeon?.prefix ?? "";
            const formatted = `${surName}, ${givenName} ${prefix}`.trim();
            const action = selectedSurgeon.is_active
              ? "deactivate"
              : "activate";
            return `Are you sure you want to ${action} ${formatted}?`;
          })()}
          onClose={() => {
            setStatusModalVisible(false);
          }}
          onConfirm={() => {
            setStatusModalVisible(false);
            if (selectedSurgeon?.is_active) {
              setReasonModalVisible(true);
            } else {
              handleStatusUpdate("");
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onDeactivateSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedSurgeon}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
};

export default Surgeons;
